import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { cisgenicTaskApi } from '@/api/config-setup/cisgenicTaskApi'
import { queryKeys, XLSX_ACEPT, XLSX_FORMAT } from '@/constant/common'
import { OPERATIONS_ROUTES } from '@/constant/route'
import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import { NotifyService } from '@/helpers'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  MenuItem,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { saveAs } from 'file-saver'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useSetUpUI } from '../../context'
import { useMk2DecoderUploadV2 } from '../../hooks/setup/useMk2DecoderUploadV2'
import useSetupUrlInterfaceId from '../../hooks/setup/useSetupUrlInterfaceId'
import SelectExcelFile from './SelectExcelFile'
import {
  runWhileSettling,
  shouldAutoResync,
  waitForImportedDecoders,
  withinTime,
} from '@/features/config/hooks/controller/mk2ImportSettle'
import useSubControllers, {
  subControllerLabel,
} from '@/features/config/hooks/controller/useSubControllers'
import classes from './styles.module.scss'
import useTranslation from '@/hooks/useTranslation'

type UploadStep = 'SELECT_FILE' | 'UPLOADING' | 'COMPLETED' | 'FAILED'

/** How often the post-import poll asks whether the decoders have landed. */
const IMPORT_SETTLE_POLL_MS = 3_000
/**
 * How long it keeps asking. 420 addresses took 23 seconds to appear on dev
 * against a report of success at 11; this leaves room for a file several times
 * that before giving up and reading once anyway.
 */
const IMPORT_SETTLE_TIMEOUT_MS = 120_000
/**
 * The automatic resync has no timeout of its own. The "waiting" flag is held
 * through it, so other syncs stay disabled while it runs; this bounds how long
 * that can last if the request never answers.
 */
const AUTO_RESYNC_FLAG_MS = 60_000

/**
 * How many decoders the interface has, as cheaply as the endpoint allows: one
 * row, read for its `totalCount`. Null when the read fails, which the caller
 * treats as "no news" rather than as a change.
 */
const readDecoderCount = async (interfaceId: number): Promise<number | null> => {
  try {
    const { data } = await controllerDataConfigApi.getAll({
      interfaceId,
      PageIndex: 1,
      PageSize: 1,
    } as any)
    const total = Number(data?.totalCount)
    return Number.isFinite(total) ? total : null
  } catch {
    return null
  }
}

const Mk2DecoderModalV2 = () => {
  const { t } = useTranslation()
  const { closeModal } = useSetUpUI()
  // SCRUM-976: pin to URL :id, not the global selected interface (AuthContext/localStorage).
  // This modal is rendered as a sibling of <Routes> (via ModalUI), not as a descendant of
  // the matched discovery/:id route, so useParams() here would always be undefined.
  const routeId = useSetupUrlInterfaceId()
  const interfaceId = Number(routeId)
  const themeMode = useSelector(selectTheme)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isDarkMode = themeMode === mode_type.dark

  const { refetch: refetchInterfaceById } = useSearchInterfaceId(String(interfaceId))

  /**
   * A control hub owns no decoders of its own — they live on the controllers
   * hanging off it, each its own interface. So the import has to be told which
   * one the file is for, and it is that controller's id the file is sent to.
   * A plain controller has no such list and imports into itself.
   */
  const { data: subControllers = [], isFetched: subControllersFetched } =
    useSubControllers(interfaceId)
  const isControlHub = subControllers.length > 0
  const [targetInterfaceId, setTargetInterfaceId] = useState<number | null>(null)
  const importInterfaceId =
    isControlHub ? targetInterfaceId : Number(interfaceId)

  const [fileNames, setFileNames] = useState<Record<string, File>>({})
  const [filePath, setFilePath] = useState<string | null>(null)
  const [step, setStep] = useState<UploadStep>('SELECT_FILE')
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  /** Re-reads scheduled after an import lands; cleared when the modal goes. */
  /** The decoder count as it stood before the import, for the poll to beat. */
  const decoderCountRef = useRef<number | null>(null)

  const uploadHook = useMk2DecoderUploadV2({
    interfaceId: Number(importInterfaceId ?? interfaceId),
    onSuccess: () => {
      setStep('COMPLETED')
      toast.success(t('decoderModalMk2ImportSuccessToast'))
      // SCRUM-1032 QC item 2 — refresh the moment the import task reports
      // Completed, not when the modal happens to unmount.
      //
      // Both keys, because they are two different reads of the same decoders:
      // `controller` backs the paged grid, and `guidedAllDecoders` is the
      // whole-list read behind the discover count and the guided Station
      // step's draft rows. The latter is held at `staleTime: Infinity` and was
      // never invalidated after an import, so it kept answering with the list
      // as it stood before the MK2 decoders arrived.
      const rereadDecoders = () => {
        queryClient.invalidateQueries([queryKeys.controller])
        queryClient.invalidateQueries([queryKeys.guidedAllDecoders])
      }
      rereadDecoders()
      /**
       * SCRUM-1032 QC, 5 Oct — wait for the decoders, do not guess at a delay.
       *
       * "Completed" is the import task's word, and on a large file the rows are
       * not queryable when it says it. Measured on D2 (6 Oct) with 420
       * addresses: the modal reported success at 11 s, the decoders reached the
       * list at **23 s**, and nothing read it again — so the screen went on
       * saying "30 decoders found" and the only way out was the Resync Wei Woon
       * reported having to press. Round 4 read again at +2s and +6s, which at
       * two decoders looked like enough and at four hundred is not.
       *
       * So the count is polled instead, cheaply (`PageSize: 1` answers with
       * `totalCount`), until it moves or the ceiling is reached. The read that
       * matters then happens once, when there is something new to read.
       */
      /**
       * Deliberately not tied to this modal's lifetime. It closes a second and
       * a half after the import reports success, and on a four-hundred-decoder
       * file the rows arrive about twenty seconds later — a poll cancelled on
       * unmount is a poll that never sees them, which is the first version of
       * this fix and why it did nothing. The query client outlives the modal,
       * so the refresh can safely happen after it has gone.
       */
      /**
       * SCRUM-1032 QC round 6, item 1 — "should add another step after import
       * and resync automatically?" (Wei Woon). Yes: decided by Jonathan on
       * 7 Oct, with the risk below put to him twice.
       *
       * Resync is `/api/autosyncdata/manual-src-objectsync-io`, which runs the
       * backend's `SyncDataJob.SyncDataIO`. That rebuilds the interface's IO
       * from what the controller reports and **deletes the rows it does not
       * mention**, guarded only by `IOList.Count == data.total` — so a
       * controller answering with one decoder and `total: 1` satisfies it. That
       * is how D2 lost its decoder list on 6 Oct. An import followed by a
       * resync against an unwell controller can therefore empty the list that
       * was just imported.
       *
       * It runs after the settle poll rather than straight after the upload:
       * the poll is already waiting for the decoder count to move off its
       * pre-import baseline, so by then the controller has answered for itself.
       * That is ordering, not a gate — it narrows the window, it does not close
       * it.
       */
      const resyncDecoders = async () => {
        try {
          await controllerDataConfigApi.getAutoSyncDecoder(
            Number(importInterfaceId ?? interfaceId),
          )
        } catch {
          // The import itself succeeded and the rows are already on screen; a
          // resync that failed is not worth a second toast contradicting the
          // first. The manual Resync button is still there.
        }
        rereadDecoders()
      }

      const startedWith = decoderCountRef.current
      const targetId = Number(importInterfaceId ?? interfaceId)
      // SCRUM-1032 QC round 7, item 11 — the list shows "waiting for the
      // imported decoders" for as long as this runs, on the screen the user
      // imported from and on the controller the file went to.
      //
      // Round 7 review: the flag is now held through the automatic resync as
      // well, so the review Resync and the decoder toolbar's Discover stay
      // disabled and cannot start a second SyncDataIO against the same
      // controller while this one runs. The resync is bounded by
      // AUTO_RESYNC_FLAG_MS so the flag always clears.
      void runWhileSettling(queryClient, [interfaceId, targetId], async () => {
        // Every outcome re-reads: on a timeout nothing arrived within the
        // ceiling — an import of addresses the controller already had looks
        // exactly like this — and the screen should at least not be older
        // than this moment.
        const outcome = await waitForImportedDecoders({
          readCount: () => readDecoderCount(targetId),
          startedWith,
          pollMs: IMPORT_SETTLE_POLL_MS,
          timeoutMs: IMPORT_SETTLE_TIMEOUT_MS,
        })
        rereadDecoders()
        // Not without a pre-import baseline: then the wait could not tell an
        // arrived import from a list that was not moving, and the resync
        // deletes what the controller does not report. Manual Resync remains.
        if (shouldAutoResync(outcome, startedWith)) {
          await withinTime(resyncDecoders(), AUTO_RESYNC_FLAG_MS)
        }
      }).catch(() => {
        // `runWhileSettling` has already lowered the flag; the import itself
        // succeeded and said so.
      })

      setTimeout(() => {
        closeModal()
      }, 1500)
    },
    onError: (error) => {
      setStep('FAILED')
      NotifyService.error(error)
    },
    onCancel: async () => {
      // After cancel completes and no active task, check interface status
      const { data: interfaceData } = await refetchInterfaceById()

      if (interfaceData?.status === true) {
        // Interface is already setup, close modal and redirect to dashboard
        closeModal()
        navigate(OPERATIONS_ROUTES.index.to)
      } else {
        // Interface not setup, show file selection
        setStep('SELECT_FILE')
      }
      toast.success(t('SetupImportCancelled'))
    },
  })

  const handleStopClick = () => {
    setShowCancelConfirm(true)
  }

  const handleConfirmCancel = async () => {
    setShowCancelConfirm(false)
    await uploadHook.cancelTask()
  }

  const handleCloseCancelConfirm = () => {
    setShowCancelConfirm(false)
  }

  const onDownload = async () => {
    await controllerDataConfigApi
      .getMk2Template(Number(importInterfaceId ?? interfaceId))
      .then((res) => {
        saveAs(res.data, 'MK2 Decoder_Template.xlsx')
      })
  }

  useEffect(() => {
    const fileKey = Object.keys(fileNames)[0]
    if (fileKey) {
      setFilePath(fileNames[fileKey].name)
    } else {
      setFilePath(null)
    }
  }, [fileNames])

  // Check for an active task once, as soon as it is known whether this is a
  // hub. On a hub the import runs on one of its controllers, not the hub, so
  // checking the hub's own id never found it: each controller is asked
  // instead, and the first one with an import running becomes the target.
  const checkedActiveRef = useRef(false)
  useEffect(() => {
    if (checkedActiveRef.current) return
    if (interfaceId > 0 && !subControllersFetched) return
    checkedActiveRef.current = true

    const findRunningImport = async (): Promise<number> => {
      for (const sub of subControllers) {
        try {
          const { data } = await cisgenicTaskApi.getActiveMk2DecoderTask(
            sub.interfaceId,
          )
          if (data?.hasActiveTask) return sub.interfaceId
        } catch {
          // A controller that cannot answer has nothing to re-attach to.
        }
      }
      return interfaceId
    }

    const checkForActiveTask = async () => {
      const checkId =
        subControllers.length > 0 ? await findRunningImport() : interfaceId
      if (subControllers.length > 0 && checkId === interfaceId) return
      const activeTask = await uploadHook.checkActiveTask(checkId)
      if (activeTask?.hasActiveTask) {
        if (checkId !== interfaceId) setTargetInterfaceId(checkId)
        setStep('UPLOADING')
        // Re-attached to an import started earlier (a reload, or the modal
        // reopened): `handleImport` never ran here, so read the baseline now.
        // The decoders land after the task reports success, so the count is
        // still the pre-import one while the task runs. If this read fails too,
        // the wait falls back to "two reads agree" and skips the auto-resync.
        decoderCountRef.current = await readDecoderCount(checkId)
      }
    }
    checkForActiveTask()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subControllersFetched])

  // Safety net for the paths that do not end in a completed import — cancel,
  // failure, or closing the modal while a task is still running. The success
  // path has already refreshed by the time this runs.
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries([queryKeys.controller])
      queryClient.invalidateQueries([queryKeys.guidedAllDecoders])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChangeFile = async (file: File[]) => {
    const newFileNames = file.reduce((acc, f) => {
      acc[f.name] = f
      return acc
    }, {} as Record<string, File>)

    // Replace instead of merge to ensure new file is selected
    setFileNames(newFileNames)
  }

  const handleImport = async () => {
    const fileKey = Object.keys(fileNames)[0]
    if (!fileKey) return
    // On a hub the file belongs to one of its controllers; without that answer
    // there is nowhere to send it.
    if (isControlHub && !targetInterfaceId) return

    setStep('UPLOADING')
    // What the list held before the import, so the poll afterwards knows when
    // the new decoders have actually arrived rather than guessing at a delay.
    decoderCountRef.current = await readDecoderCount(
      Number(importInterfaceId ?? interfaceId),
    )
    await uploadHook.upload(fileNames[fileKey])
  }

  const handleClose = () => {
    if (step === 'UPLOADING') {
      return
    }
    closeModal()
  }

  const renderContent = () => {
    if (step === 'UPLOADING') {
      return (
        <Stack spacing={2} width="100%">
          {/* Title - always show */}
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '14px',
              color: isDarkMode ? '#fff' : '#000',
            }}
          >
            {t('decoderModalMk2Title')}
          </Typography>

          {uploadHook.overallProgress === 0 ? (
            <Stack alignItems="center" spacing={2} py={4}>
              <CircularProgress size={48} />
              <Typography
                sx={{
                  fontSize: '14px',
                  color: isDarkMode ? '#B0BEC5' : '#5c5c5c',
                }}
              >
                {t('SetupUploadingFile')}
              </Typography>
            </Stack>
          ) : (
            <Stack width="100%" spacing={2} py={2}>
              {/* Progress bar */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress
                      color="primary"
                      variant={
                        uploadHook.overallProgress > 0
                          ? 'determinate'
                          : 'indeterminate'
                      }
                      value={uploadHook.overallProgress}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 45 }}>
                    <Typography variant="body2" color="text.main">
                      {`${Math.round(uploadHook.overallProgress)}%`}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Description below progress bar */}
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '120%',
                  textAlign: 'center',
                  color: isDarkMode ? '#B0BEC5' : '#5c5c5c',
                }}
              >
                {t('decoderModalMk2PleaseWait')}
              </Typography>

              {/* Stop button */}
              <Stack direction="row" justifyContent="center" mt={1}>
                <Button
                  variant="secondary"
                  onClick={handleStopClick}
                  disabled={uploadHook.isCancelling}
                >
                  {uploadHook.isCancelling
                    ? t('SetupStopping')
                    : t('commonLabelsStop')}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      )
    }

    if (step === 'COMPLETED') {
      return (
        <Stack alignItems="center" py={4}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#4caf50',
            }}
          >
            {t('decoderModalMk2CompletedMessage')}
          </Typography>
        </Stack>
      )
    }

    if (step === 'FAILED') {
      return (
        <Stack spacing={2}>
          <Typography className={classes.txtImportMk2}>
            {t('decoderModalMk2Title')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#f44336',
            }}
          >
            {uploadHook.error || t('decoderModalMk2FailedMessage')}
          </Typography>
          <Button variant="secondary" sx={{ mt: '20px' }}>
            <SelectExcelFile
              accept={XLSX_ACEPT}
              formats={XLSX_FORMAT}
              onChange={onChangeFile}
              filePath={filePath}
            />
          </Button>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            mt="33px"
          >
            <Button variant="secondary" onClick={handleClose}>
              {t('commonActionsCancel')}
            </Button>
            <Button
              variant="primary"
              className={classes.modalButton}
              disabled={!filePath}
              onClick={handleImport}
            >
              {t('tryAgain')}
            </Button>
          </Stack>
        </Stack>
      )
    }

    // SELECT_FILE step
    return (
      <Stack sx={{ gap: '18px' }}>
        <Stack sx={{ gap: '4px' }}>
          <Typography className={classes.txtImportMk2}>
            {t('decoderModalMk2Title')}
          </Typography>
          <Typography className={classes.txtContentImport}>
            {isControlHub
              ? t('decoderModalMk2DescriptionHub')
              : t('decoderModalMk2Description')}
          </Typography>
        </Stack>

        {isControlHub && (
          <Stack sx={{ gap: '6px' }}>
            <Typography className={classes.fieldLabel}>
              {t('formsLabelsController')}
            </Typography>
            <Select
              value={targetInterfaceId ? String(targetInterfaceId) : ''}
              displayEmpty
              onChange={(event) =>
                setTargetInterfaceId(Number(event.target.value) || null)
              }
              renderValue={(value) => {
                const sub = subControllers.find(
                  (candidate) => String(candidate.interfaceId) === value,
                )
                return sub ? (
                  subControllerLabel(sub)
                ) : (
                  <Box component="span" sx={{ color: 'grey.600' }}>
                    {t('decoderModalMk2ControllerPlaceholder')}
                  </Box>
                )
              }}
              sx={{
                height: 40,
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'background.default',
              }}
            >
              {subControllers.map((sub) => (
                <MenuItem
                  key={sub.interfaceId}
                  value={String(sub.interfaceId)}
                  sx={{ fontSize: '14px' }}
                >
                  {subControllerLabel(sub)}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}

        <SelectExcelFile
          panel
          disabled={isControlHub && !targetInterfaceId}
          hint={t('decoderModalMk2FileHint')}
          accept={XLSX_ACEPT}
          formats={XLSX_FORMAT}
          onChange={onChangeFile}
          filePath={filePath}
        />

        {/* A link, not a button: it fetches a file and changes nothing here. */}
        <Typography
          component="button"
          type="button"
          onClick={onDownload}
          className={classes.linkDownload}
        >
          {t('decoderModalMk2DownloadTemplate')}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: '6px' }}
        >
          <Button
            variant="secondary"
            className={classes.modalButton}
            onClick={handleClose}
          >
            {t('commonActionsCancel')}
          </Button>
          <Button
            variant="primary"
            className={classes.modalButton}
            disabled={!filePath || (isControlHub && !targetInterfaceId)}
            onClick={handleImport}
          >
            {t('commonActionsImport')}
          </Button>
        </Stack>
      </Stack>
    )
  }

  return (
    <>
      <Stack
        className={classes.rootMk2Modal}
        // Height from the content: the fixed 263px was cut for the file
        // chooser as it was, and left a hole under the shorter states.
        sx={{ minHeight: step === 'SELECT_FILE' ? 0 : '200px' }}
      >
        {renderContent()}
      </Stack>

      {/* Cancel confirmation dialog */}
      <Dialog open={showCancelConfirm} onClose={handleCloseCancelConfirm}>
        <Box sx={{ backgroundColor: 'background.default' }}>
          <DialogTitle sx={{ padding: '41px 152px 4px 34px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '20px',
                lineHeight: '24px',
                minHeight: '24px',
                minWidth: '192px',
              }}
            >
              {t('SetupCancelImportTitle')}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ paddingLeft: '34px', paddingBottom: '18px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '1.3',
                maxWidth: '314px',
              }}
            >
              {t('decoderModalMk2CancelConfirmMessage')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ paddingBottom: '32px', paddingRight: '30px' }}>
            <Button
              variant="secondary"
              sx={{ width: '99px' }}
              onClick={handleCloseCancelConfirm}
            >
              {t('decoderModalMk2CancelConfirmNo')}
            </Button>
            <Button
              variant="primary"
              sx={{ width: '99px' }}
              onClick={handleConfirmCancel}
            >
              {t('decoderModalMk2CancelConfirmYes')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}

export default Mk2DecoderModalV2
